// src/components/chat/SearchInput.jsx
import React, { useState, useEffect, useCallback, useId } from 'react';
import styles from './SearchInput.module.css';

/**
 * Debounced search input.
 *
 * Props:
 *  - onSearch(query: string) — called after the debounce delay
 *  - placeholder             — input placeholder text
 *  - debounceMs              — debounce delay in ms (default: 300)
 */
export default function SearchInput({
  onSearch,
  placeholder = 'Search users...',
  debounceMs = 300,
}) {
  const [query, setQuery] = useState('');
  const inputId = useId(); // stable unique ID for the label association

  // Fire onSearch after the user stops typing
  useEffect(() => {
    const timer = setTimeout(() => {
      onSearch(query);
    }, debounceMs);
    return () => clearTimeout(timer);
  }, [query, onSearch, debounceMs]);

  const handleChange = (e) => setQuery(e.target.value);

  const handleClear = useCallback(() => {
    setQuery('');
    onSearch('');
  }, [onSearch]);

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') handleClear();
  };

  return (
    <div className={styles.searchContainer} role="search">
      
      <span className={styles.searchIcon} aria-hidden="true">🔍</span>
      <input
        id={inputId}
        type="text"
        className={styles.searchInput}
        placeholder={placeholder}
        value={query}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        autoComplete="off"
      />
      {query && (
        <button
          className={styles.clearBtn}
          onClick={handleClear}
          type="button"
          aria-label="Clear search"
        >
          ✕
        </button>
      )}
    </div>
  );
}